import { Organization, ApiError } from '../types/organization';

/**
 * Адрес сервиса оргструктуры. Литерал здесь означал бы, что ЛЮБАЯ инсталляция
 * спрашивает список организаций у боевого контура KT: на отдельном контуре в
 * выпадающем списке появлялись чужие клиенты, а членство бралось не из своей БД.
 * Дефолт сохраняет прежнее поведение. Пустая строка — API на этом же хосте
 * (ROUTING_MODE=paths, ci/routing-mode.sh): запросы уходят на /api/ витрины.
 */
const ORG_STRUCTURE_API_URL: string =
  ((import.meta as any).env?.VITE_ORG_STRUCTURE_API_URL as string) ?? 'https://org-structure.osno-va.com';

/**
 * Получает email пользователя из Auth MFE (аналогично OrgStructure)
 */
function getUserEmail(): string | null {
  // 1. Пробуем window.auth_user.state.user
  const userFromWindow = (window as any).auth_user?.state?.user;
  if (userFromWindow?.email) {
    return userFromWindow.email;
  }
  
  // 2. Пробуем localStorage (state.user)
  try {
    const userStr = localStorage.getItem('auth_user');
    if (userStr) {
      const userObj = JSON.parse(userStr);
      const userFromStorage = userObj?.state?.user;
      if (userFromStorage?.email) {
        return userFromStorage.email;
      }
    }
  } catch {
    // игнорируем ошибки парсинга
  }
  
  return null;
}

/**
 * API клиент для работы с организациями
 * Использует тот же подход что и OrgStructure MFE для получения email
 */
export class OrganizationApi {
  async getOrganizations(): Promise<Organization[]> {
    console.log('🔄 [OrganizationApi] Starting organization fetch...');

    // Получаем email пользователя
    const userEmail = getUserEmail();
    if (!userEmail) {
      console.error('❌ [OrganizationApi] userEmail не найден в localStorage или window.auth_user');
      throw {
        detail: 'User email not found. Please login first.',
        status: 401,
      } as ApiError;
    }

    console.log('✅ [OrganizationApi] Found user email:', userEmail);

    // Подход 1: Прямой API вызов (самый надежный)
          try {
        console.log(`🔄 [OrganizationApi] Trying direct API call to ${ORG_STRUCTURE_API_URL}...`);
        const response = await fetch(`${ORG_STRUCTURE_API_URL}/api/organization-structures/`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-User-Email': userEmail,
        },
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      console.log('✅ [OrganizationApi] Direct API success:', data);
      
      // Адаптируем формат ответа к нашим типам
      const organizations = data.map((org: any) => ({
        organization_id: parseInt(org.id, 10),
        organization_name: org.name,
      }));

      console.log('✅ [OrganizationApi] Mapped organizations:', organizations);
      return organizations;
    } catch (apiError) {
      console.warn('⚠️ [OrganizationApi] Direct API failed:', apiError);
    }

    // Подход 2: Через предзагруженный OrgStructure store
    try {
      console.log('🔄 [OrganizationApi] Trying OrgStructure store...');
      
      // Проверяем глобальные переменные или store
      const globalStore = (window as any).__ORG_STRUCTURE_STORE__;
      if (globalStore && globalStore.organizations) {
        console.log('✅ [OrganizationApi] Found organizations in global store:', globalStore.organizations);
        return globalStore.organizations.map((org: any) => ({
          organization_id: org.id,
          organization_name: org.name,
        }));
      }
    } catch (storeError) {
      console.warn('⚠️ [OrganizationApi] Store approach failed:', storeError);
    }

    // Подход 3: Mock данные для разработки (временно)
    console.warn('⚠️ [OrganizationApi] All approaches failed, returning mock data');
    return [
      { organization_id: 1, organization_name: 'Тестовая организация 1' },
      { organization_id: 2, organization_name: 'Тестовая организация 2' },
    ];
  }
}

export const organizationApi = new OrganizationApi(); 